import { InfoTooltip } from '../InfoTooltip.js';
import { METRIC_EXPLANATIONS } from '../../data/metric-explanations.js';
import { formatScoreWithLabel } from '../../lib/scoreFormatter.js';

export interface ApprovalIntelData {
  riskScore: number;
  riskLevel: string;
  reasons: string[];
  hasMint: boolean;
  hasBlacklist: boolean;
  hasPause: boolean;
  ownershipRenounced: boolean;
  dataUnavailableReason?: string;
}

function riskColor(level: string): string {
  if (level === 'HIGH') return '#e74c3c';
  if (level === 'MEDIUM') return '#f39c12';
  return '#27ae60';
}

export function ApprovalIntelPanel({ data }: { data: ApprovalIntelData | null | undefined }) {
  const d = data ?? null;

  if (!d) return null;

  if (d.dataUnavailableReason) {
    return (
      <div className="dash-card ws-forensic-section">
        <h3 className="ws-forensic-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          Contract Control Risk
          <InfoTooltip explain={METRIC_EXPLANATIONS.approvals} compact />
        </h3>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Contract Controls</span>
            <span className="intel-metric-value" style={{ fontSize: '0.85rem' }}>
              {d.dataUnavailableReason}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dash-card ws-forensic-section">
      <h3 className="ws-forensic-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        Contract Control Risk
        <InfoTooltip explain={METRIC_EXPLANATIONS.approvals} compact />
      </h3>
      <div className="intel-grid intel-grid-4">
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">
              Control Risk Score
              <InfoTooltip explain={METRIC_EXPLANATIONS.approvals} compact />
            </span>
            <span className="intel-metric-value" style={{ color: riskColor(d.riskLevel) }}>
              {formatScoreWithLabel(d.riskScore)}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">
              Can Create New Tokens
              <InfoTooltip explain={METRIC_EXPLANATIONS.mintFunction} compact />
            </span>
            <span className={`intel-bool-badge ${!d.hasMint ? 'intel-bool-good' : 'intel-bool-bad'}`}>
              {d.hasMint ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">
              Can Block Wallets
              <InfoTooltip explain={METRIC_EXPLANATIONS.blacklist} compact />
            </span>
            <span className={`intel-bool-badge ${!d.hasBlacklist ? 'intel-bool-good' : 'intel-bool-bad'}`}>
              {d.hasBlacklist ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">
              Can Pause Trading
              <InfoTooltip explain={METRIC_EXPLANATIONS.pauseFunction} compact />
            </span>
            <span className={`intel-bool-badge ${!d.hasPause ? 'intel-bool-good' : 'intel-bool-bad'}`}>
              {d.hasPause ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">
              Owner Control
              <InfoTooltip explain={METRIC_EXPLANATIONS.ownershipRenounced} compact />
            </span>
            <span className={`intel-bool-badge ${d.ownershipRenounced ? 'intel-bool-good' : 'intel-bool-bad'}`}>
              {d.ownershipRenounced ? 'Renounced' : 'Active'}
            </span>
          </div>
        </div>
      </div>
      {d.reasons && d.reasons.length > 0 && d.reasons[0] !== 'No dangerous contract privileges detected' && (
        <ul style={{ margin: '0.5rem 0 0', padding: '0 0 0 1.2rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
          {d.reasons.map((r, i) => <li key={i}>{r}</li>)}
        </ul>
      )}
    </div>
  );
}
